import type {
  CitationPreview,
  CitationsData,
  RenderedCitation,
  WebCitation,
  WebCitationPassage,
} from "@esi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  BookOpenText,
  Bot,
  Check,
  CircleDashed,
  ExternalLink,
  FolderClosed,
  Globe,
  ListTree,
  LoaderCircle,
  Lock,
  type LucideIcon,
  Paperclip,
  TriangleAlert,
} from "lucide-react";
import {
  type FocusEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { formatDate } from "../i18n/format.ts";
import { openHrefOf, useSourceViewer } from "../projects/SourceViewer.tsx";
import { useOpenWiki } from "../router.tsx";
import { GeneratedMark } from "../trust/GeneratedMark.tsx";
import { useAttachmentContext } from "./attachments/AttachmentControls.tsx";
import { accessVersion, attachmentKeys, clearAttachmentAccess } from "./attachments/queries.ts";
import { attachmentSessionGeneration } from "./attachments/session.ts";
import { useCitationOpen } from "./citation-open.ts";
import { type CitationState, citationHref, stateText } from "./citation-state.ts";
import { locationLabel, provenanceLabel } from "./citations.ts";
import { ClassificationMark, useClassificationLabel } from "./classification.tsx";
import { safeHref } from "./tool-approvals.ts";
import { isWebHandle } from "./web-results.ts";

const STATE_STYLE: Record<CitationState, { chip: string; text: string; icon: LucideIcon }> = {
  pending: {
    chip: "border-dashed border-line-strong text-fg-muted",
    text: "text-fg-muted",
    icon: CircleDashed,
  },
  checking: {
    chip: "border-dashed border-line-strong text-fg-secondary",
    text: "text-fg-secondary",
    icon: LoaderCircle,
  },
  supported: {
    chip: "border-success-border/45 bg-success-bg text-success-fg",
    text: "text-success-fg",
    icon: Check,
  },
  unsupported: {
    chip: "border-warning-border/60 bg-warning-bg text-warning-fg",
    text: "text-warning-fg",
    icon: TriangleAlert,
  },
  unchecked: {
    chip: "border-line bg-surface-sunken text-fg-secondary",
    text: "text-fg-secondary",
    icon: CircleDashed,
  },
  deleted: {
    chip: "border-line bg-surface-sunken text-fg-muted",
    text: "text-fg-muted",
    icon: Ban,
  },
  unavailable: {
    chip: "border-line bg-surface-sunken text-fg-muted",
    text: "text-fg-muted",
    icon: Ban,
  },
};

/** The state's icon; the checking spinner stays still when reduced motion is asked for. */
export function StateIcon({ state, className }: { state: CitationState; className?: string }) {
  const Icon = STATE_STYLE[state].icon;
  return (
    <Icon
      aria-hidden="true"
      strokeWidth={2}
      className={cn(
        "size-3 shrink-0",
        state === "checking" && "motion-safe:animate-spin",
        className,
      )}
    />
  );
}

export function chipClass(state: CitationState) {
  return STATE_STYLE[state].chip;
}

export function stateTextClass(state: CitationState) {
  return STATE_STYLE[state].text;
}

/** Where the preview reads the cited passage; absent in contexts without a stored message. */
export interface PassageRef {
  /** Where its sources open (wiki, downloads); empty outside a project, which cites none. */
  projectId: string;
  conversationId: string;
  messageId: string;
}

/**
 * The cited passage, read through the checked endpoint (wiki pages included). The chip observes
 * the same cache entry without fetching, so a refusal seen in the preview reaches the chip too.
 */
export function usePassage(
  handle: string,
  passage: PassageRef | null,
  enabled: boolean,
  attachment = false,
) {
  const client = useQueryClient();
  return useQuery({
    queryKey: ["citation-passage", passage?.conversationId, passage?.messageId, handle] as const,
    enabled,
    staleTime: 0,
    retry: false,
    queryFn: async ({ signal }): Promise<CitationPreview> => {
      const ref = passage as PassageRef;
      const generation = attachmentSessionGeneration();
      // An attachment refusal while this read runs fences it, as `freshAttachmentList` (#207 R5).
      const version = attachment ? accessVersion(client, ref.conversationId) : 0;
      const current = () =>
        !signal.aborted &&
        generation === attachmentSessionGeneration() &&
        version === (attachment ? accessVersion(client, ref.conversationId) : 0);
      try {
        const result = await ok(
          await api.api.conversations[":conversationId"].messages[":messageId"].citations[
            ":handle"
          ].$get(
            {
              param: { conversationId: ref.conversationId, messageId: ref.messageId, handle },
            },
            { init: { signal } },
          ),
        );
        if (!current()) {
          throw new Error("session_changed");
        }
        return result;
      } catch (error) {
        if (
          current() &&
          error instanceof ApiError &&
          (error.status === 403 || (error.status === 404 && error.explained))
        ) {
          if (attachment) {
            clearAttachmentAccess(client, ref.conversationId);
            void client.invalidateQueries({ queryKey: attachmentKeys.list(ref.conversationId) });
          }
          void client.invalidateQueries({ queryKey: ["conversation", ref.conversationId] });
        }
        throw error;
      }
    },
  });
}

/**
 * The server answers every refusal like a missing citation: the source is gone from view. A
 * bare 404 (an older API without the route, during a canary) only means no preview text.
 */
const isRefusal = (error: unknown) =>
  error instanceof ApiError && (error.status === 403 || (error.status === 404 && error.explained));

const OPEN_DELAY = 250;
const CLOSE_DELAY = 150;

/**
 * The preview behaviour every citation chip shares: it peeks on mouse hover (after a delay) and
 * on keyboard focus (staying open while focused), and pins on tap or Enter, moving focus into
 * it. Escape or an outside click closes it, and focus returns to the chip after a pinned one.
 */
export function usePreviewPopover(options: { focusList?: boolean } = {}) {
  // `peek`: shown without taking focus (hover, focus); `pinned`: opened on purpose (click, Enter).
  const [mode, setMode] = useState<"closed" | "peek" | "pinned">("closed");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDivElement>(null);
  // Focus coming back to the chip after closing must not reopen it as a peek.
  const returning = useRef(false);
  // Whether the preview being closed was pinned (focus then returns to the chip).
  const wasPinned = useRef(false);
  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  // A pending hover timer must not fire after the chip unmounts.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    [],
  );
  const later = (fn: () => void, ms: number) => {
    clear();
    timer.current = setTimeout(fn, ms);
  };
  const peek = () => setMode((m) => (m === "closed" ? "peek" : m));
  // Pinned on purpose (Enter, tap): focus moves into the preview, so its actions are reachable.
  useEffect(() => {
    if (mode === "pinned") {
      wasPinned.current = true;
      // The control that opens the source first (a link, or the viewer's button), else the
      // first action ("Show in sources"). A group's list takes focus itself: focusing its first
      // chip would open that chip's preview.
      const inside = content.current;
      (options.focusList
        ? inside
        : (inside?.querySelector<HTMLElement>("[data-open-source]") ??
          inside?.querySelector<HTMLElement>("a[href]") ??
          inside?.querySelector<HTMLElement>("a, button"))
      )?.focus();
    } else if (mode === "peek") {
      wasPinned.current = false;
    }
  }, [mode, options.focusList]);
  const leave = () => later(() => setMode((m) => (m === "peek" ? "closed" : m)), CLOSE_DELAY);
  return {
    open: mode !== "closed",
    chip,
    /** Closes it without returning focus to the chip (the caller moves focus elsewhere). */
    dismiss: () => {
      clear();
      wasPinned.current = false;
      setMode("closed");
    },
    root: {
      open: mode !== "closed",
      onOpenChange: (open: boolean) => setMode(open ? "pinned" : "closed"),
    },
    trigger: {
      ref: chip,
      "aria-expanded": mode !== "closed",
      "aria-haspopup": "dialog" as const,
      onPointerEnter: (e: PointerEvent<HTMLButtonElement>) => {
        if (e.pointerType === "mouse") {
          later(peek, OPEN_DELAY);
        }
      },
      onPointerLeave: (e: PointerEvent<HTMLButtonElement>) => {
        if (e.pointerType === "mouse") {
          leave();
        }
      },
      onFocus: (e: FocusEvent<HTMLButtonElement>) => {
        if (returning.current) {
          returning.current = false;
          return;
        }
        if (e.currentTarget.matches(":focus-visible")) {
          clear();
          peek();
        }
      },
      onBlur: () => setMode((m) => (m === "peek" ? "closed" : m)),
      onClick: () => {
        clear();
        setMode((m) => (m === "pinned" ? "closed" : "pinned"));
      },
    },
    content: {
      ref: content,
      collisionPadding: 12,
      onOpenAutoFocus: (e: Event) => e.preventDefault(),
      onCloseAutoFocus: (e: Event) => {
        e.preventDefault();
        // Focus already moved on (a source row reached from a preview inside a group): kept.
        const active = document.activeElement;
        const away =
          active !== null && active !== document.body && !content.current?.contains(active);
        if (wasPinned.current && !away) {
          wasPinned.current = false;
          returning.current = true;
          chip.current?.focus();
        }
      },
      onInteractOutside: (e: { target: EventTarget | null; preventDefault: () => void }) => {
        // The chip toggles itself; a press on it is not an outside click.
        if (chip.current?.contains(e.target as Node)) {
          e.preventDefault();
        }
      },
      onPointerEnter: clear,
      onPointerLeave: (e: PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === "mouse") {
          leave();
        }
      },
    },
  };
}

export const CHIP_CLASS =
  "mx-0.5 inline-flex h-[1.15rem] translate-y-[-1px] items-center gap-0.5 rounded-[5px] border px-1 align-middle text-[11px] leading-none font-semibold tabular-nums transition-colors duration-(--duration-fast) hover:brightness-[0.97] dark:hover:brightness-110";

/**
 * A citation chip: a button whose preview opens on hover (pointer), on keyboard focus (and
 * stays open while focused) and on tap or Enter (moving focus into it). Escape closes it and
 * focus returns to the chip. Opening the source is a separate link inside the preview.
 */
export function CitationChip(props: {
  handle: string;
  citation: RenderedCitation | undefined;
  /** A `W#` handle's web citation (once the citations part is in). */
  web?: WebCitation | undefined;
  /** The snippet the web search returned for a `W#` handle, shown before the stored one loads. */
  webSnippet?: string | null;
  data: CitationsData | null;
  state: CitationState;
  passage: PassageRef | null;
  canOpen: (documentId: string) => boolean;
  /**
   * The sources row's chip: the source's title shows truncated inside it (and its domain is
   * not repeated for a web result).
   */
  titled?: boolean;
  /** Expands the answer's sources and moves to this source's row (the preview's action). */
  onShowInSources?: (handle: string) => void;
}) {
  const { handle, citation, web, webSnippet, data, passage, canOpen, titled } = props;
  const isWeb = isWebHandle(handle);
  const { t } = useTranslation();
  const popover = usePreviewPopover();

  // A refusal seen while previewing (access narrowed since the answer) updates the chip too.
  const observed = usePassage(
    handle,
    props.passage,
    false,
    !!citation && "attachment" in citation && citation.attachment === true,
  );
  // A web citation is public content: a refused preview never hides it.
  const gone = !isWeb && isRefusal(observed.error);
  const state: CitationState = gone ? "unavailable" : props.state;
  const srState = gone
    ? t("citations.state.unavailable")
    : citation || web
      ? stateText(state, citation)
      : t("citations.state.pending");
  const readable = citation !== undefined && citation.title !== null && !gone;
  const generated = readable && "generated" in citation && citation.generated === true;
  // The sources row's chip only: a lock for a level above the scheme's lowest (said by the
  // server, so guests get it too); inline and group chips carry no level.
  const level = useClassificationLabel(readable ? citation.classification : undefined);
  const locked = titled === true && readable && citation.aboveLowest === true;
  // Shown in full inside a titled chip, so not read twice.
  const shownTitle = titled
    ? web
      ? (web.title ?? web.domain)
      : readable
        ? citation.title
        : null
    : null;
  const label = web
    ? [
        // The sources row's chip shows its title after the handle: the title leads its name
        // there (WCAG 2.5.3), the inline chip starts with what it is.
        ...(titled ? [web.title, t("citations.web.badge")] : [t("citations.web.badge"), web.title]),
        web.domain,
        web.publishedAt ? formatDate(web.publishedAt) : null,
        srState,
      ]
        .filter(Boolean)
        .join(" · ")
    : readable
      ? [
          citation.title,
          locked && level ? t("citations.classificationLock", { label: level }) : null,
          generated ? t("generated.mark") : null,
          locationLabel(citation.location),
          provenanceLabel(citation.provenance),
          srState,
        ]
          .filter(Boolean)
          .join(" · ")
      : srState;
  const showInSources = props.onShowInSources
    ? () => {
        popover.dismiss();
        props.onShowInSources?.(handle);
      }
    : undefined;

  return (
    <Popover {...popover.root}>
      <PopoverAnchor asChild>
        <button
          type="button"
          {...popover.trigger}
          // The sources row's chip is named apart from the inline one: "Source S2: …".
          aria-label={titled ? t("citations.sourceChip", { handle, label }) : undefined}
          className={cn(
            CHIP_CLASS,
            titled && "mx-0 h-6 max-w-full min-w-0 translate-y-0 gap-1 px-1.5",
            STATE_STYLE[state].chip,
          )}
        >
          <StateIcon state={state} />
          {locked ? (
            // Its level is in the chip's accessible name.
            <Lock aria-hidden="true" className="size-2.5 shrink-0" strokeWidth={2} />
          ) : null}
          {handle}
          {readable && citation.attachment ? (
            <Paperclip aria-label={t("attachments.marker")} className="size-2.5 shrink-0" />
          ) : null}
          {generated ? (
            // Written by the agent: the label below says it in words to screen readers.
            <span
              aria-hidden="true"
              className="ms-0.5 inline-flex items-center border-l border-current/25 ps-1"
            >
              <Bot className="size-2.5 shrink-0" strokeWidth={2} />
            </span>
          ) : null}
          {web && !titled ? (
            // The domain says where it comes from; the label below says it to screen readers.
            <span
              aria-hidden="true"
              className="ms-0.5 inline-flex max-w-[10rem] items-center gap-0.5 border-l border-current/25 ps-1 font-medium"
            >
              <Globe className="size-2.5 shrink-0" strokeWidth={2} />
              <span className="truncate">{web.domain}</span>
            </span>
          ) : null}
          {shownTitle ? (
            <>
              {/* The space keeps the handle and the title two words for the name check (WCAG
                  2.5.3); the flex layout does not render it. */}{" "}
              <span className="ms-0.5 inline-flex min-w-0 items-center gap-1 border-l border-current/25 ps-1.5 text-xs font-medium">
                {web ? (
                  <Globe aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
                ) : null}
                <span aria-hidden="true" className="max-w-[9.5rem] truncate sm:max-w-[12rem]">
                  {shownTitle}
                </span>
              </span>
            </>
          ) : null}
          {titled ? null : <span className="sr-only"> ({label})</span>}
        </button>
      </PopoverAnchor>
      <PopoverContent
        {...popover.content}
        side="top"
        align="start"
        className="w-[min(24rem,calc(100vw-2rem))] p-0"
        aria-label={t("citations.preview.label", { handle })}
      >
        {isWeb ? (
          <WebPreview
            handle={handle}
            web={web}
            snippet={webSnippet ?? null}
            state={state}
            passage={passage}
            onShowInSources={showInSources}
          />
        ) : (
          <Preview
            handle={handle}
            citation={citation}
            data={data}
            state={state}
            passage={passage}
            canOpen={canOpen}
            onShowInSources={showInSources}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Weakest first: unverified, then checking, then unchecked, then supported. */
const GROUP_RANK: Record<CitationState, number> = {
  unsupported: 0,
  checking: 1,
  unchecked: 2,
  pending: 2,
  unavailable: 2,
  deleted: 2,
  supported: 3,
};

/** The state a group of sources shows: its weakest one (pending and gone ones as unchecked). */
export function weakestState(states: readonly CitationState[]): CitationState {
  const weakest = [...states].sort((a, b) => GROUP_RANK[a] - GROUP_RANK[b])[0] ?? "unchecked";
  return GROUP_RANK[weakest] === 2 ? "unchecked" : weakest;
}

/** One source of a group chip: its handle, what it is, and its state. */
export interface GroupSource {
  handle: string;
  /** Its title or domain; null when the viewer cannot read it. */
  title: string | null;
  state: CitationState;
  /**
   * Its own chip, opening its own preview; `close` closes the group first (its preview's Show
   * in sources moves focus to the row, never back to the group chip).
   */
  chip: (close: () => void) => ReactNode;
}

/**
 * A run citing more than three distinct sources, as one chip: "13 sources" with the weakest
 * state's icon and colour. Its popover lists each source (its chip opening its own preview,
 * the title, the state). Same peek, pin and Escape behaviour as a single chip.
 */
export function CitationGroupChip(props: { sources: readonly GroupSource[] }) {
  const { sources } = props;
  const { t } = useTranslation();
  const popover = usePreviewPopover({ focusList: true });
  const state = weakestState(sources.map((s) => s.state));
  const unverified = sources.filter((s) => s.state === "unsupported").length;
  const name = [
    t("citations.group", { count: sources.length }),
    unverified > 0 ? t("citations.verdicts.unverified", { count: unverified }) : null,
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <Popover {...popover.root}>
      <PopoverAnchor asChild>
        <button
          type="button"
          {...popover.trigger}
          aria-label={name}
          className={cn(CHIP_CLASS, STATE_STYLE[state].chip)}
        >
          <StateIcon state={state} />
          {t("citations.group", { count: sources.length })}
        </button>
      </PopoverAnchor>
      <PopoverContent
        {...popover.content}
        side="top"
        align="start"
        tabIndex={-1}
        className="w-[min(20rem,calc(100vw-2rem))] p-0 outline-none"
        aria-label={t("citations.groupLabel", { count: sources.length })}
      >
        <ul className="flex max-h-72 flex-col divide-y divide-line overflow-y-auto py-1">
          {sources.map((s) => (
            <li
              key={s.handle}
              className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2 gap-y-0.5 px-3 py-1.5"
            >
              <span className="pt-0.5">{s.chip(popover.dismiss)}</span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span
                  className={cn(
                    "text-[13px] leading-snug break-words",
                    s.title === null ? "text-fg-muted italic" : "font-medium text-fg",
                  )}
                >
                  {s.title ?? t("citations.preview.unavailable")}
                </span>
                {s.title === null || s.state === "pending" ? null : (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-xs font-medium",
                      stateTextClass(s.state),
                    )}
                  >
                    <StateIcon state={s.state} />
                    {t(`citations.state.${s.state}`)}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** The preview's quiet "Show in sources" action, beside the link that opens the source. */
function ShowInSources({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-surface-hover hover:text-fg"
    >
      <ListTree aria-hidden="true" className="size-3" />
      {t("citations.showInSources")}
    </button>
  );
}

function Preview(props: {
  handle: string;
  citation: RenderedCitation | undefined;
  data: CitationsData | null;
  state: CitationState;
  passage: PassageRef | null;
  canOpen: (documentId: string) => boolean;
  onShowInSources?: (() => void) | undefined;
}) {
  const { handle, citation, state, passage, canOpen, onShowInSources } = props;
  const attachmentContext = useAttachmentContext();
  const { t } = useTranslation();
  const view = useSourceViewer();
  const { show: showWiki } = useOpenWiki();
  const openTarget = useCitationOpen(
    citation,
    passage?.projectId ?? "",
    passage?.conversationId || undefined,
  );
  const readable = citation !== undefined && citation.title !== null;
  const excerpt = usePassage(
    handle,
    passage,
    readable && passage !== null,
    !!citation && "attachment" in citation && citation.attachment === true,
  );
  const refused = isRefusal(excerpt.error);
  const noRoute =
    excerpt.error instanceof ApiError && excerpt.error.status === 404 && !excerpt.error.explained;
  const shownState: CitationState = refused ? "unavailable" : state;
  // This preview reads passages only; a web snippet has its own preview.
  const passageData = excerpt.data && !("kind" in excerpt.data) ? excerpt.data : undefined;

  if (!readable || refused) {
    return (
      <div className="flex flex-col gap-1 p-3">
        <p className="flex items-center gap-1.5 text-sm font-medium text-fg">
          <StateIcon state={shownState} className="size-3.5 text-fg-muted" />
          {state === "deleted" && !refused
            ? t("citations.state.deleted")
            : t("citations.preview.unavailable")}
        </p>
        <p className="text-xs text-fg-secondary">{t("citations.preview.unavailableBody")}</p>
        {onShowInSources ? (
          <div className="-mx-1 -mb-1 flex justify-end pt-1">
            <ShowInSources onClick={onShowInSources} />
          </div>
        ) : null}
      </div>
    );
  }
  const provenance = provenanceLabel(citation.provenance);
  const attachment = citation.attachment
    ? attachmentContext?.list?.attachments.find((a) => a.id === citation.documentId)
    : undefined;
  const opens = citation.attachment
    ? attachment?.status === "ready" && attachment.downloadAllowed
    : citation.kind === "wiki_page" || canOpen(citation.documentId);
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1 border-b border-line px-3 pt-3 pb-2.5">
        <p className="text-sm leading-snug font-medium break-words text-fg">
          {citation.title}
          {citation.classification ? (
            <>
              {" "}
              <ClassificationMark id={citation.classification} className="ms-1 -translate-y-px" />
            </>
          ) : null}
        </p>
        {citation.attachment ? (
          <p className="flex items-center gap-1 text-xs text-fg-muted">
            <Paperclip aria-hidden="true" className="size-3" />
            {t("attachments.marker")} · {t("attachments.path")}
          </p>
        ) : null}
        {!citation.attachment && passageData?.path && passageData.path.length > 0 ? (
          // Its folders, read live (knowledge-folders): cut from the left, the nearest stays.
          <p
            className="flex min-w-0 items-center gap-1 text-xs text-fg-muted"
            title={passageData.path.join(" › ")}
          >
            <FolderClosed aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
            <span className="sr-only">{t("citations.preview.folder")} </span>
            <span dir="rtl" className="min-w-0 truncate text-left">
              <bdi dir="ltr">{passageData.path.join(" › ")}</bdi>
            </span>
          </p>
        ) : null}
        <p className="text-xs text-fg-muted">
          {[locationLabel(citation.location), provenance].filter(Boolean).join(" · ")}
        </p>
        {citation.generated ? (
          <p className="flex text-xs text-fg-muted">
            <GeneratedMark />
          </p>
        ) : null}
        <p
          className={cn(
            "inline-flex items-center gap-1 text-xs font-medium",
            stateTextClass(shownState),
          )}
        >
          <StateIcon state={shownState} />
          {stateText(shownState, citation)}
        </p>
      </div>
      <div className="flex flex-col gap-2 px-3 py-2.5">
        {citation.generated ? (
          <p className="text-xs text-fg-secondary">{t("generated.previewNote")}</p>
        ) : null}
        {excerpt.isPending && excerpt.fetchStatus !== "idle" ? (
          <p className="text-xs text-fg-muted">{t("citations.preview.loading")}</p>
        ) : passageData?.versionChanged ? (
          <p className="text-xs text-fg-secondary">{t("citations.preview.versionChanged")}</p>
        ) : passageData?.excerpt ? (
          <blockquote
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling quote must be keyboard-scrollable (axe scrollable-region-focusable).
            tabIndex={0}
            className="max-h-48 overflow-y-auto border-l-2 border-line-strong pl-2.5 text-[13px] leading-relaxed whitespace-pre-wrap text-fg-secondary"
          >
            {passageData.excerpt}
          </blockquote>
        ) : noRoute ? (
          <p className="text-xs text-fg-muted">{t("citations.preview.noExcerpt")}</p>
        ) : excerpt.isError ? (
          <p className="text-xs text-danger-fg">{excerpt.error.message}</p>
        ) : excerpt.isSuccess ? (
          <p className="text-xs text-fg-muted">{t("citations.preview.noExcerpt")}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-x-1 border-t border-line px-2 py-1.5">
        {onShowInSources ? <ShowInSources onClick={onShowInSources} /> : null}
        {opens && passage && openTarget?.mode === "tab" ? (
          <a
            data-open-source
            href={openHrefOf(openTarget.target)}
            referrerPolicy="no-referrer"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-surface-hover"
          >
            {t("citations.openSource")}
            <ExternalLink aria-hidden="true" className="size-3" />
            <span className="sr-only">{t("app.newTab")}</span>
          </a>
        ) : opens && passage && openTarget?.mode === "viewer" ? (
          <button
            data-open-source
            type="button"
            onClick={() => view(openTarget.target)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-surface-hover"
          >
            {t("citations.openSource")}
            <BookOpenText aria-hidden="true" className="size-3" />
          </button>
        ) : opens && passage && citation.kind === "wiki_page" && passage.conversationId ? (
          // A wiki page opens beside the conversation (wiki-collaboration D8a).
          <button
            data-open-source
            type="button"
            onClick={() =>
              showWiki({
                kind: "page",
                pageId: citation.documentId.toLowerCase(),
                version: citation.version,
              })
            }
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-surface-hover"
          >
            {t("citations.openSource")}
            <BookOpenText aria-hidden="true" className="size-3" />
          </button>
        ) : opens && passage ? (
          <a
            data-open-source
            href={citationHref(passage.projectId, citation, passage.conversationId)}
            referrerPolicy="no-referrer"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-surface-hover"
          >
            {t("citations.openSource")}
            <ExternalLink aria-hidden="true" className="size-3" />
            <span className="sr-only">{t("app.newTab")}</span>
          </a>
        ) : (
          <span className="px-2 py-1 text-xs text-fg-muted">{t("files.citeOnly")}</span>
        )}
      </div>
    </div>
  );
}

/** The "web" mark of a web citation: a small info lozenge with a globe, beside its domain. */
export function WebBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex h-4 shrink-0 items-center gap-0.5 rounded-[5px] border border-info-border/50 bg-info-bg px-1 text-[11px] leading-none font-semibold text-info-fg",
        className,
      )}
    >
      <Globe aria-hidden="true" className="size-2.5" strokeWidth={2} />
      {t("citations.web.badge")}
    </span>
  );
}

/** "example.com · 3 Mar 2026": where a web result comes from and when it was published. */
export function webMeta(domain: string | null | undefined, publishedAt: string | null | undefined) {
  return [domain, publishedAt ? formatDate(publishedAt) : null].filter(Boolean).join(" · ");
}

/**
 * A web citation's preview: the "web" badge, domain and published date, the title, the check's
 * verdict, the snippet the agent received (as stored with the message: the page is never
 * fetched again), when it was retrieved, and a link that opens the page in a new tab.
 */
function WebPreview(props: {
  handle: string;
  web: WebCitation | undefined;
  snippet: string | null;
  state: CitationState;
  passage: PassageRef | null;
  onShowInSources?: (() => void) | undefined;
}) {
  const { handle, web, snippet, state, passage, onShowInSources } = props;
  const { t } = useTranslation();
  const stored = usePassage(handle, passage, web !== undefined && passage !== null);
  const data: WebCitationPassage | undefined =
    stored.data && "kind" in stored.data && stored.data.kind === "web" ? stored.data : undefined;
  const title = data?.title ?? web?.title ?? null;
  const domain = data?.domain ?? web?.domain ?? null;
  const href = safeHref(data?.url ?? web?.url);
  const excerpt = data?.excerpt ?? snippet;
  const meta = webMeta(domain, data?.publishedAt ?? web?.publishedAt);
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1 border-b border-line px-3 pt-3 pb-2.5">
        <p className="flex min-w-0 items-center gap-1.5 text-xs text-fg-muted">
          <WebBadge />
          {meta ? <span className="min-w-0 truncate tabular-nums">{meta}</span> : null}
        </p>
        {title ? (
          <p className="text-sm leading-snug font-medium break-words text-fg">{title}</p>
        ) : null}
        <p
          className={cn(
            "inline-flex items-center gap-1 text-xs font-medium",
            stateTextClass(state),
          )}
        >
          <StateIcon state={state} />
          {stateText(state, undefined)}
        </p>
      </div>
      <div className="flex flex-col gap-2 px-3 py-2.5">
        {stored.isPending && stored.fetchStatus !== "idle" && !excerpt ? (
          <p className="text-xs text-fg-muted">{t("citations.preview.loading")}</p>
        ) : excerpt ? (
          <blockquote
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling quote must be keyboard-scrollable (axe scrollable-region-focusable).
            tabIndex={0}
            className="max-h-48 overflow-y-auto border-l-2 border-line-strong pl-2.5 text-[13px] leading-relaxed break-words whitespace-pre-wrap text-fg-secondary"
          >
            {excerpt}
          </blockquote>
        ) : (
          <p className="text-xs text-fg-muted">{t("citations.web.noSnippet")}</p>
        )}
        {data?.retrievedAt ? (
          <p className="text-xs text-fg-muted tabular-nums">
            {t("citations.web.retrieved", { date: formatDate(data.retrievedAt) })}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-x-1 border-t border-line px-2 py-1.5">
        {onShowInSources ? <ShowInSources onClick={onShowInSources} /> : null}
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-surface-hover"
          >
            {t("citations.web.open")}
            <ExternalLink aria-hidden="true" className="size-3" />
            <span className="sr-only">{t("app.newTab")}</span>
          </a>
        ) : (
          <span className="px-2 py-1 text-xs text-fg-muted">{t("citations.web.noLink")}</span>
        )}
      </div>
    </div>
  );
}
